/**
 * Organizations API — client functions for the Organization Admin feature.
 *
 * All functions use the shared `api` instance from '@/lib/api/client' which
 * automatically unwraps the standard `{ success, data }` envelope, so callers
 * receive the typed payload directly.
 *
 * The CSV export function uses `api.getBlob` which sets `responseType: 'blob'`
 * and returns a raw `Blob` (bypassing the envelope interceptor).
 */

import { api } from '@/lib/api/client';
import type {
  Organization,
  OrganizationMember,
  MyOrgInfo,
  RegisterOrganizationPayload,
  JoinOrganizationPayload,
  SetCoursePricingPayload,
  CoursePricing,
  CourseCatalogItem,
  CourseDetail,
  CalculatePricePayload,
  PriceCalculation,
  PurchaseCoursePayload,
  PurchaseCourseResponse,
  PurchaseStatusResponse,
  CoursePurchase,
  AssignCoursePayload,
  AssignCourseBatchPayload,
  AssignCourseBatchResponse,
  AssignmentWithProgress,
  MemberProgress,
  MemberProgressGrouped,
  MemberSummary,
  OrganizationDashboard,
  CourseShare,
  ShareCoursePayload,
  UpdateOrganizationPayload,
} from '../types';

// ============================================================
// Organization endpoints
// ============================================================

/** POST /organizations/register — register a new organization (admin only) */
export function registerOrganization(
  dto: RegisterOrganizationPayload,
): Promise<Organization> {
  return api.post<Organization>('/organizations/register', dto);
}

/** POST /organizations/join — join an existing organization via code */
export function joinOrganization(dto: JoinOrganizationPayload): Promise<OrganizationMember> {
  return api.post<OrganizationMember>('/organizations/join', dto);
}

/** GET /organizations/me — get the current user's organization */
export function getMyOrganization(): Promise<Organization> {
  return api.get<Organization>('/organizations/me');
}

/** PATCH /organizations/me — update organization details (admin only) */
export function updateMyOrganization(dto: UpdateOrganizationPayload): Promise<Organization> {
  return api.patch<Organization>('/organizations/me', dto);
}

/** GET /organizations/my-org — get org info for the current user (admin or member) */
export function getMyOrgInfo(): Promise<MyOrgInfo> {
  return api.get<MyOrgInfo>('/organizations/my-org');
}

/** POST /organizations/logo — upload a logo for the organization (admin only) */
export function uploadOrgLogo(file: File): Promise<Organization> {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<Organization>('/organizations/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** GET /organizations/members — list all members of the current user's organization */
export function getOrganizationMembers(): Promise<OrganizationMember[]> {
  return api.get<OrganizationMember[]>('/organizations/members');
}

/** GET /organizations/dashboard — aggregate dashboard data for org admin */
export function getOrganizationDashboard(): Promise<OrganizationDashboard> {
  return api.get<OrganizationDashboard>('/organizations/dashboard');
}

/** GET /organizations/members-summary — summary with progress for all members */
export function getMembersSummary(): Promise<MemberSummary[]> {
  return api.get<MemberSummary[]>('/organizations/members-summary');
}

// ============================================================
// Course catalog & pricing endpoints
// ============================================================

/** GET /courses/catalog — list published courses available for purchase */
export function getCatalog(): Promise<CourseCatalogItem[]> {
  return api.get<CourseCatalogItem[]>('/courses/catalog');
}

/** GET /courses/catalog/:deckListId — get detailed information about a specific course */
export function getCourseDetail(deckListId: string): Promise<CourseDetail> {
  return api.get<CourseDetail>(`/courses/catalog/${deckListId}`);
}

/** POST /courses/calculate-price — preview price for a bulk purchase */
export function calculatePrice(dto: CalculatePricePayload): Promise<PriceCalculation> {
  return api.post<PriceCalculation>('/courses/calculate-price', dto);
}

// ============================================================
// Purchase endpoints
// ============================================================

/** POST /courses/purchase — purchase course seats */
export function purchaseCourse(dto: PurchaseCoursePayload): Promise<PurchaseCourseResponse> {
  return api.post<PurchaseCourseResponse>('/courses/purchase', dto);
}

/** GET /courses/purchases/:id/status — check purchase status after Stripe redirect */
export function getPurchaseStatus(purchaseId: string): Promise<PurchaseStatusResponse> {
  return api.get<PurchaseStatusResponse>(`/courses/purchases/${purchaseId}/status`);
}

/** GET /courses/purchases — list all purchases for the current organization */
export function getMyPurchases(): Promise<CoursePurchase[]> {
  return api.get<CoursePurchase[]>('/courses/purchases');
}

// ============================================================
// Assignment endpoints
// ============================================================

/** POST /organizations/assign-course — assign purchased course seats to a member */
export function assignCourse(dto: AssignCoursePayload): Promise<AssignmentWithProgress> {
  return api.post<AssignmentWithProgress>('/organizations/assign-course', dto);
}

/** POST /organizations/assign-course-batch — assign purchased course to multiple members (Issue 159) */
export function assignCourseBatch(dto: AssignCourseBatchPayload): Promise<AssignCourseBatchResponse> {
  return api.post<AssignCourseBatchResponse>('/organizations/assign-course-batch', dto);
}

/** DELETE /organizations/assignments/:id — remove an assignment */
export function unassignCourse(assignmentId: string): Promise<void> {
  return api.delete(`/organizations/assignments/${assignmentId}`);
}

/** GET /organizations/assignments — list all assignments with progress data */
export function getAssignments(): Promise<AssignmentWithProgress[]> {
  return api.get<AssignmentWithProgress[]>('/organizations/assignments');
}

// ============================================================
// Member progress & export endpoints
// ============================================================

/** GET /organizations/members/:memberId/progress — detailed progress for a member */
export function getMemberProgress(memberId: string): Promise<MemberProgress> {
  return api.get<MemberProgress>(`/organizations/members/${memberId}/progress`);
}

/** GET /organizations/members/:memberId/progress-grouped — grouped progress by course */
export function getMemberProgressGrouped(memberId: string): Promise<MemberProgressGrouped> {
  return api.get<MemberProgressGrouped>(`/organizations/members/${memberId}/progress-grouped`);
}

/** GET /organizations/export-activity — download organization activity as CSV */
export function exportMemberActivity(): Promise<Blob> {
  return api.getBlob('/organizations/export-activity');
}

/** GET /organizations/members/:memberId/report-pdf — download member progress PDF report */
export function downloadMemberReport(memberId: string): Promise<Blob> {
  return api.getBlob(`/organizations/members/${memberId}/report-pdf`);
}

/** GET /organizations/members/:memberId/certificate/:deckListId — download course completion certificate PDF */
export function downloadCertificate(memberId: string, deckListId: string): Promise<Blob> {
  return api.getBlob(`/organizations/members/${memberId}/certificate/${deckListId}`);
}

/** GET /organizations/members/:memberId/courses/:deckListId/report-pdf — download single course PDF report */
export function downloadSingleCourseReport(memberId: string, deckListId: string): Promise<Blob> {
  return api.getBlob(`/organizations/members/${memberId}/courses/${deckListId}/report-pdf`);
}

// ============================================================
// Course pricing management endpoints
// ============================================================

/** POST /courses/:deckListId/pricing — set or update pricing for a course */
export function setCoursePricing(
  deckListId: string,
  payload: SetCoursePricingPayload,
): Promise<CoursePricing> {
  return api.post<CoursePricing>(`/courses/${deckListId}/pricing`, payload);
}

/** GET /courses/:deckListId/pricing — get current pricing for a course */
export function getCoursePricing(deckListId: string): Promise<CoursePricing> {
  return api.get<CoursePricing>(`/courses/${deckListId}/pricing`);
}

// ============================================================
// Course sharing endpoints (Issue 157)
// ============================================================

/** POST /courses/:deckListId/share — share a course with an organization via code */
export function shareCourse(
  deckListId: string,
  dto: ShareCoursePayload,
): Promise<CourseShare> {
  return api.post<CourseShare>(`/courses/${deckListId}/share`, dto);
}

/** DELETE /courses/shares/:id — remove a course share */
export function unshareCourse(shareId: string): Promise<void> {
  return api.delete(`/courses/shares/${shareId}`);
}

/** GET /courses/:deckListId/shares — list all shares for a specific course */
export function listCourseShares(deckListId: string): Promise<CourseShare[]> {
  return api.get<CourseShare[]>(`/courses/${deckListId}/shares`);
}

/** GET /courses/shared-courses — list all shared courses for the organization */
export function listSharedCourses(): Promise<CourseShare[]> {
  return api.get<CourseShare[]>('/courses/shared-courses');
}

// ============================================================
// Member management endpoints (Issue 177)
// ============================================================

/** PATCH /organizations/members/:memberId/disable — disable a member */
export function disableMember(memberId: string): Promise<{ success: boolean }> {
  return api.patch<{ success: boolean }>(`/organizations/members/${memberId}/disable`);
}

/** PATCH /organizations/members/:memberId/enable — enable a member */
export function enableMember(memberId: string): Promise<{ success: boolean }> {
  return api.patch<{ success: boolean }>(`/organizations/members/${memberId}/enable`);
}

/** DELETE /organizations/members/:memberId — delete a member and all associated data */
export function deleteMember(memberId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/organizations/members/${memberId}`);
}